/** Calculates filled and cancelled quantities for trade toasts and Agent order outcomes. */

import { ClientNotificationType, NotificationType } from "@decibeltrade/sdk";

interface OrderSizes {
  orig_size?: number | null;
  remaining_size?: number | null;
  size_delta?: number | null;
}

interface TwapSizes {
  orig_size?: number | null;
  remaining_size?: number | null;
}

const REMOVED_SIZE_DELTA_TYPES: Array<NotificationType | ClientNotificationType> = [
  NotificationType.OrderCancelled,
  NotificationType.OrderErrored,
  ClientNotificationType.OrderCancellationErrored,
  ClientNotificationType.PositionCancellationErrored,
  ClientNotificationType.PositionReversalErrored,
  ClientNotificationType.TwapCancellationErrored,
];

export const usesRemovedSizeDelta = (type: NotificationType | ClientNotificationType) =>
  REMOVED_SIZE_DELTA_TYPES.includes(type);

export const getFilledSize = (
  type: NotificationType | ClientNotificationType,
  order?: OrderSizes,
): number | null => {
  const origSize = order?.orig_size ?? null;
  // Position-wide TP/SL has no fixed size; leave its rendering to the caller.
  if (origSize === null) return null;
  if (type === NotificationType.OrderRejected) return 0;
  // Cancellation zeroes remaining_size; size_delta is the removed remainder.
  if (usesRemovedSizeDelta(type)) return origSize - (order?.size_delta ?? origSize);

  // A fill's size_delta covers one match, so use remaining_size for the cumulative fill.
  return origSize - (order?.remaining_size ?? 0);
};

export const getCancelledSize = (
  type: NotificationType | ClientNotificationType,
  order?: OrderSizes,
  twap?: TwapSizes,
): number | null => {
  if (usesRemovedSizeDelta(type)) return order?.size_delta ?? null;

  const origSize = order?.orig_size ?? twap?.orig_size ?? null;
  const remainingSize = order?.remaining_size ?? twap?.remaining_size ?? null;
  // Preserve a known size when only one field is missing.
  if (origSize === null && remainingSize === null) return null;

  return (origSize ?? 0) - (remainingSize ?? 0);
};
